import React, { useMemo, useRef, useState, useCallback } from 'react';
import { chartElementAtEvent } from '../chartInteraction';
import type { ActiveElement, Chart as ChartJS, ChartEvent, TooltipItem } from 'chart.js';
import { getRelativePosition } from 'chart.js/helpers';
import { Bar } from 'react-chartjs-2';
import KpiChartPanel from './KpiChartPanel';
import { formatAxisCompactK, formatDataLabelCompact } from '../chartFormatters';

/**
 * Bar chart fills — opaque RGB. Aligned with `KpiRateDonuts`: ongoing comm = amber, resolution = purple.
 */
export const KPI_SLA_METRIC_COLORS = {
  case: 'rgb(30, 64, 175)',
  ongoingComm: 'rgb(217, 119, 6)',
  resolution: 'rgb(147, 51, 234)',
} as const;

/**
 * `skipNull` + second value `null` = one physical bar for single-segment groups (Case).
 * Low `categoryPercentage` keeps all groups visually slim on the chart.
 * `barPercentage: 1` removes gaps inside Incident / Request pairs.
 */
const BAR_PAIR_LAYOUT = {
  categoryPercentage: 0.4,
  barPercentage: 1,
  borderWidth: 0,
  inflateAmount: 'auto' as const,
  skipNull: true,
} as const;

export interface KpiSlaBreachBarSegment {
  label: string;
  value: number;
  color: string;
  rateBreachedDrillKey: string;
}

export interface KpiSlaBreachBarGroup {
  /** Tick under the pair (e.g. Case, Incident, Request) */
  axisLabel: string;
  /** One segment (Case) or two (Incident / Request); two bars in a group share one category with no gap */
  segments: KpiSlaBreachBarSegment[];
}

export interface KpiSlaByTypeBarChartProps {
  title: string;
  groups: KpiSlaBreachBarGroup[];
  onBarClick?: (rateBreachedDrillKey: string) => void;
}

/** X-axis groups — same idea as monthly trend series (Case / Incident / Request). */
const SLA_TYPE_KEYS = ['Case', 'Incident', 'Request'] as const;
export type KpiSlaBreachTypeKey = (typeof SLA_TYPE_KEYS)[number];

const R = 6;

/** Match `KpiMonthlyTrendChart` legend: `labels.boxWidth` / `boxHeight` 10, `font.size` 12, `font.weight` 500, `color` #475569 */
const LEGEND_TEXT_STYLE: React.CSSProperties = {
  color: '#475569',
  fontSize: 13,
  fontWeight: 400,
  lineHeight: 1,
};

const LEGEND_DOT =
  'inline-block h-[14px] w-[14px] shrink-0 box-border rounded-full border-2 border-solid bg-white';

type SlaBreachBarModel = {
  labels: string[];
  d0: number[];
  d1: (number | null)[];
  c0: string[];
  c1: string[];
  seg0label: string[];
  seg1label: string[];
  drill0: string[];
  drill1: string[];
  /** True when category only renders dataset 0 (Case) */
  singleBar: boolean[];
  maxY: number;
};

function buildModel(groups: KpiSlaBreachBarGroup[]): SlaBreachBarModel {
  const labels = groups.map((g) => g.axisLabel);
  const singleBar: boolean[] = [];
  const d0: number[] = [];
  const d1: (number | null)[] = [];
  const c0: string[] = [];
  const c1: string[] = [];
  const seg0label: string[] = [];
  const seg1label: string[] = [];
  const drill0: string[] = [];
  const drill1: string[] = [];

  for (const g of groups) {
    const a = g.segments[0];
    const b = g.segments[1];
    if (g.segments.length === 1 && a) {
      singleBar.push(true);
      d0.push(a.value);
      d1.push(null);
      c0.push(a.color);
      c1.push('transparent');
      seg0label.push(a.label);
      seg1label.push('');
      drill0.push(a.rateBreachedDrillKey);
      drill1.push('');
    } else if (a && b) {
      singleBar.push(false);
      d0.push(a.value);
      d1.push(b.value);
      c0.push(a.color);
      c1.push(b.color);
      seg0label.push(a.label);
      seg1label.push(b.label);
      drill0.push(a.rateBreachedDrillKey);
      drill1.push(b.rateBreachedDrillKey);
    } else {
      singleBar.push(true);
      d0.push(0);
      d1.push(null);
      c0.push('rgba(148, 163, 184, 0.5)');
      c1.push('transparent');
      seg0label.push('');
      seg1label.push('');
      drill0.push('');
      drill1.push('');
    }
  }

  const maxY = Math.max(0, ...groups.flatMap((gr) => gr.segments.map((s) => s.value)));
  return { labels, singleBar, d0, d1, c0, c1, seg0label, seg1label, drill0, drill1, maxY };
}

const defaultTypeVisibility = (): Record<KpiSlaBreachTypeKey, boolean> => ({
  Case: true,
  Incident: true,
  Request: true,
});

const KpiSlaByTypeBarChart: React.FC<KpiSlaByTypeBarChartProps> = ({ title, groups, onBarClick }) => {
  const chartRef = useRef<ChartJS<'bar'>>(null);
  const modelRef = useRef<SlaBreachBarModel>(buildModel([]));
  const onDrillRef = useRef(onBarClick);
  const skipCanvasClickRef = useRef(false);

  const [typeVisible, setTypeVisible] = useState<Record<KpiSlaBreachTypeKey, boolean>>(defaultTypeVisibility);

  const filteredGroups = useMemo(() => {
    return SLA_TYPE_KEYS.flatMap((key) => {
      if (!typeVisible[key]) return [];
      const g = groups.find((gr) => gr.axisLabel === key);
      return g ? [g] : [];
    });
  }, [groups, typeVisible]);

  const toggleType = useCallback((key: KpiSlaBreachTypeKey) => {
    setTypeVisible((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      const onCount = SLA_TYPE_KEYS.filter((k) => next[k]).length;
      if (onCount === 0) return prev;
      return next;
    });
  }, []);

  const model = useMemo(() => buildModel(filteredGroups), [filteredGroups]);
  modelRef.current = model;
  onDrillRef.current = onBarClick;

  const labelListeners = useMemo(
    () => ({
      enter(context: any, event: any) {
        const chart = context.chart as ChartJS<'bar'>;
        const m = modelRef.current;
        if (context.datasetIndex === 1 && m.d1[context.dataIndex] == null) {
          return false;
        }
        context.hovered = true;
        const pos =
          event && typeof event.x === 'number' && typeof event.y === 'number'
            ? { x: event.x, y: event.y }
            : event?.native
              ? getRelativePosition(event.native, chart)
              : { x: 0, y: 0 };
        const active = { datasetIndex: context.datasetIndex, index: context.dataIndex };
        chart.setActiveElements([active]);
        chart.tooltip?.setActiveElements([active], pos);
        if (onDrillRef.current) {
          chart.canvas.style.cursor = 'pointer';
        }
        chart.update();
        return true;
      },
      leave(context: any, event: any) {
        context.hovered = false;
        const chart = context.chart as ChartJS<'bar'>;
        const di = context.dataIndex;
        const dsi = context.datasetIndex;
        const ev = event?.native ?? event;
        let keepTooltip = false;
        if (ev instanceof MouseEvent) {
          const els = chart.getElementsAtEventForMode(ev, 'nearest', { intersect: true }, false);
          keepTooltip = els.some((e) => e.index === di && e.datasetIndex === dsi);
        }
        if (!keepTooltip) {
          chart.setActiveElements([]);
          chart.tooltip?.setActiveElements([], { x: 0, y: 0 });
          chart.canvas.style.cursor = 'default';
        }
        chart.update();
        return true;
      },
      click(context: any, event: any) {
        const m = modelRef.current;
        if (context.datasetIndex === 1 && m.d1[context.dataIndex] == null) {
          return false;
        }
        skipCanvasClickRef.current = true;
        window.setTimeout(() => {
          skipCanvasClickRef.current = false;
        }, 0);
        const key =
          context.datasetIndex === 0 ? m.drill0[context.dataIndex] : m.drill1[context.dataIndex];
        if (key) {
          onDrillRef.current?.(key);
        }
        event?.native?.stopPropagation?.();
        return true;
      },
    }),
    []
  );

  const datalabelColor = (ctx: any) => (ctx.hovered ? 'rgb(15, 23, 42)' : '#334155');
  const datalabelFont = (ctx: any) => ({
    weight: ctx.hovered ? 700 : 600,
    size: 10,
  });

  const handleClick = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!onBarClick || !chartRef.current || skipCanvasClickRef.current) return;
    const els = chartElementAtEvent(chartRef.current, event);
    const el = els[0];
    if (el == null) return;
    const key = el.datasetIndex === 0 ? model.drill0[el.index] : model.drill1[el.index];
    if (!key) return;
    onBarClick(key);
  };

  const barOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { top: 8, bottom: 4 } },
      elements: {
        bar: {
          borderWidth: 0,
        },
      },
      onHover: (event: ChartEvent, elements: ActiveElement[]) => {
        const native = event.native;
        const target = native && 'target' in native ? native.target : null;
        if (!(target instanceof HTMLElement)) return;
        const chart = chartRef.current;
        const tooltipActive = (chart?.tooltip?.getActiveElements?.()?.length ?? 0) > 0;
        target.style.cursor =
          onBarClick && (elements.length > 0 || tooltipActive) ? 'pointer' : 'default';
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          intersect: true,
          mode: 'nearest' as const,
          filter: (item: { datasetIndex: number; dataIndex: number }) =>
            item.datasetIndex === 0 || model.d1[item.dataIndex] != null,
          callbacks: {
            title: (items: TooltipItem<'bar'>[]) => {
              const item = items[0];
              if (!item) return '';
              const di = item.dataIndex;
              const dsi = item.datasetIndex;
              const lineLabel = dsi === 0 ? model.seg0label[di] : model.seg1label[di];
              return lineLabel || model.labels[di] || '';
            },
            label: (item: TooltipItem<'bar'>) => {
              const n = Number(item.raw);
              const formatted = Number.isFinite(n) ? formatDataLabelCompact(n) : String(item.raw);
              return `Breaches: ${formatted}`;
            },
          },
        },
      },
      scales: {
        x: {
          offset: true,
          grid: { display: false },
          ticks: {
            color: '#64748b',
            maxRotation: 0,
            minRotation: 0,
            autoSkip: false,
            font: { size: 11, weight: 500 },
          },
        },
        y: {
          display: true,
          beginAtZero: true,
          suggestedMax: model.maxY > 0 ? model.maxY * 1.12 : undefined,
          ticks: {
            precision: 0,
            color: '#94a3b8',
            callback: (v: string | number) => formatAxisCompactK(v as number),
          },
          grid: { color: 'rgba(148, 163, 184, 0.15)' },
          border: { display: false },
        },
      },
    }),
    [model, onBarClick]
  );

  return (
    <KpiChartPanel title={title} className="h-full lg:col-span-1" flexFill>
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="relative min-h-[280px] w-full shrink-0 flex-1 sm:min-h-[320px]">
        <Bar
          ref={chartRef}
          onClick={onBarClick ? handleClick : undefined}
          data={{
            labels: model.labels,
            datasets: [
              {
                label: 'SLA breach (1)',
                data: model.d0,
                backgroundColor: model.c0,
                hoverBackgroundColor: model.c0,
                borderSkipped: false,
                ...BAR_PAIR_LAYOUT,
                hoverBorderWidth: 0,
                borderRadius: (ctx) => {
                  const r = R;
                  const di = ctx.dataIndex;
                  if (model.singleBar[di]) {
                    return { topLeft: r, topRight: r, bottomLeft: 0, bottomRight: 0 };
                  }
                  return { topLeft: r, bottomLeft: 0, topRight: r, bottomRight: 0 };
                },
                datalabels: {
                  display: (ctx) => {
                    const v = Number(ctx.dataset.data[ctx.dataIndex]);
                    return Number.isFinite(v) && v > 0;
                  },
                  anchor: 'end',
                  align: 'end',
                  offset: 4,
                  color: datalabelColor,
                  font: datalabelFont,
                  listeners: labelListeners as any,
                  formatter: (value: number) => formatDataLabelCompact(Number(value)),
                },
              },
              {
                label: 'SLA breach (2)',
                data: model.d1,
                backgroundColor: model.c1,
                hoverBackgroundColor: model.c1,
                borderSkipped: false,
                ...BAR_PAIR_LAYOUT,
                hoverBorderWidth: 0,
                borderRadius: (ctx) => {
                  const r = R;
                  const di = ctx.dataIndex;
                  if (model.singleBar[di]) {
                    return { topLeft: 0, topRight: 0, bottomLeft: 0, bottomRight: 0 };
                  }
                  return { topLeft: r, bottomLeft: 0, topRight: r, bottomRight: 0 };
                },
                datalabels: {
                  display: (ctx) => {
                    const raw = ctx.dataset.data[ctx.dataIndex];
                    if (raw == null) return false;
                    const v = Number(raw);
                    return Number.isFinite(v) && v > 0;
                  },
                  anchor: 'end',
                  align: 'end',
                  offset: 4,
                  color: datalabelColor,
                  font: datalabelFont,
                  listeners: labelListeners as any,
                  formatter: (value: number) => formatDataLabelCompact(Number(value)),
                },
              },
            ],
          }}
          options={barOptions}
        />
        </div>
        <div
          className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 px-2 pb-1 pt-2"
          role="group"
          aria-label="Filter SLA breach chart by ticket type"
        >
          {SLA_TYPE_KEYS.map((key) => {
            const on = typeVisible[key];
            return (
              <button
                key={key}
                type="button"
                aria-pressed={on}
                onClick={() => toggleType(key)}
                className={`inline-flex items-center gap-2 transition-opacity ${on ? 'opacity-100' : 'opacity-45'}`}
              >
                {key === 'Case' ? (
                  <span
                    className={LEGEND_DOT}
                    style={{ borderColor: KPI_SLA_METRIC_COLORS.case }}
                    aria-hidden
                  />
                ) : (
                  <span className="inline-flex items-center gap-1" aria-hidden>
                    <span
                      className={LEGEND_DOT}
                      style={{ borderColor: KPI_SLA_METRIC_COLORS.ongoingComm }}
                    />
                    <span
                      className={LEGEND_DOT}
                      style={{ borderColor: KPI_SLA_METRIC_COLORS.resolution }}
                    />
                  </span>
                )}
                <span style={LEGEND_TEXT_STYLE}>{key}</span>
              </button>
            );
          })}
        </div>
      </div>
    </KpiChartPanel>
  );
};

export default KpiSlaByTypeBarChart;
